import * as PIXI from 'pixi.js';
import {getFixturePortalProjection} from './portalCollision.js';
import {portalNormal, portalTangent} from './portalMath.js';

const DEFAULT_PORTAL_COLOR = '#0093ff';
const PLANE_TOLERANCE = 0.04;
const SEGMENT_MERGE_TOLERANCE = 0.05;
const MAX_PARTICLES = 64;
const MAX_SPAWNS_PER_FRAME = 7;

const clamp01 = value => Math.max(0, Math.min(1, value));

export const lightenPortalColor = (color, amount = 0.58) => {
	let rgb;
	try {
		rgb = new PIXI.Color(color || DEFAULT_PORTAL_COLOR).toArray();
	} catch(_error) {
		rgb = new PIXI.Color(DEFAULT_PORTAL_COLOR).toArray();
	}
	const mix = clamp01(amount);
	const channel = value => Math.round((value + (1 - value) * mix) * 255);
	return (channel(rgb[0]) << 16) | (channel(rgb[1]) << 8) | channel(rgb[2]);
};

export const mergePortalIntersectionSegments = (
	segments,
	tolerance = SEGMENT_MERGE_TOLERANCE,
) => {
	const sorted = segments
		.filter(segment => Number.isFinite(segment.min) && Number.isFinite(segment.max))
		.map(segment => ({
			min: Math.min(segment.min, segment.max),
			max: Math.max(segment.min, segment.max),
		}))
		.sort((left, right) => left.min - right.min);
	const merged = [];
	for(const segment of sorted) {
		const previous = merged[merged.length - 1];
		if(previous && segment.min <= previous.max + tolerance) {
			previous.max = Math.max(previous.max, segment.max);
		} else {
			merged.push(segment);
		}
	}
	return merged;
};

export const getPortalIntersectionSegments = (
	Box2D,
	bodies,
	endpoint,
	planeTolerance = PLANE_TOLERANCE,
) => {
	if(!endpoint?.body || !bodies) return [];
	const nullPointer = Box2D.getPointer(Box2D.NULL);
	const aperture = endpoint.apertureHalfLength;
	const segments = [];
	for(const body of bodies) {
		if(!body || body.destroyed) continue;
		for(let fixture = body.GetFixtureList();
			Box2D.getPointer(fixture) !== nullPointer;
			fixture = fixture.GetNext()) {
			if(fixture.IsSensor()) continue;
			const projection = getFixturePortalProjection(Box2D, fixture, endpoint);
			if(projection.normal.min > planeTolerance ||
				projection.normal.max < -planeTolerance) {
				continue;
			}
			const min = Math.max(-aperture, projection.tangent.min);
			const max = Math.min(aperture, projection.tangent.max);
			if(max >= min) segments.push({min, max});
		}
	}
	return mergePortalIntersectionSegments(segments);
};

const topLevelDisplay = (display, renderParent) => {
	let current = display;
	while(current?.parent && current.parent !== renderParent) current = current.parent;
	return current?.parent === renderParent ? current : null;
};

// The effect belongs immediately above the highest top-level display branch
// used by the travelling island. It therefore follows editor layer ordering
// without becoming a global foreground overlay.
export const placePortalParticlesAboveDisplays = (
	container,
	displays,
	renderParent,
) => {
	if(!container || !renderParent) return null;
	const roots = [...new Set(displays
		.filter(display => display && !display.destroyed)
		.map(display => topLevelDisplay(display, renderParent))
		.filter(Boolean))];
	let anchor = null;
	let anchorIndex = -1;
	for(const root of roots) {
		const index = renderParent.getChildIndex(root);
		if(index > anchorIndex) {
			anchor = root;
			anchorIndex = index;
		}
	}
	if(!anchor) {
		if(container.parent !== renderParent) renderParent.addChild(container);
		return null;
	}
	if(container.parent === renderParent &&
		renderParent.getChildIndex(container) === anchorIndex + 1) {
		return anchor;
	}
	container.removeFromParent();
	anchorIndex = renderParent.getChildIndex(anchor);
	renderParent.addChildAt(container, Math.min(anchorIndex + 1, renderParent.children.length));
	return anchor;
};

const endpointColor = endpoint => endpoint?.prefabObject?.settings?.color ||
	endpoint?.body?.mySprite?.data?.colorFill?.[0] || DEFAULT_PORTAL_COLOR;

const chooseSegment = (segments, totalLength) => {
	let distance = Math.random() * totalLength;
	for(const segment of segments) {
		const length = Math.max(0.02, segment.max - segment.min);
		if(distance <= length) return segment;
		distance -= length;
	}
	return segments[segments.length - 1];
};

export class PortalIntersectionParticles {
	constructor({
		Box2D,
		parent,
		ptm,
		getBodies,
		getEndpoint,
		depthDisplays = [],
		depthParent = null,
		label = 'portal-intersection-particles',
	}) {
		this.Box2D = Box2D;
		this.ptm = ptm;
		this.getBodies = getBodies;
		this.getEndpoint = getEndpoint;
		this.depthDisplays = depthDisplays;
		this.depthParent = depthParent;
		this.container = new PIXI.Container();
		this.container.label = label;
		this.container.eventMode = 'none';
		this.active = [];
		this.pool = [];
		this.spawnAccumulator = 2;
		parent.addChild(this.container);
		this.syncDepth();
	}

	syncDepth() {
		if(this.depthParent) {
			placePortalParticlesAboveDisplays(
				this.container,
				this.depthDisplays,
				this.depthParent,
			);
		}
	}

	drawParticle(particle, color, droplet) {
		const soft = lightenPortalColor(color, 0.24);
		const light = lightenPortalColor(color, 0.43);
		const hot = lightenPortalColor(color, 0.72);
		particle.clear();
		particle.ellipse(0, 0, 6.4, 4.7).fill({color: soft, alpha: 0.18});
		if(droplet) {
			particle.moveTo(5.8, 0)
				.bezierCurveTo(2.5, -3.8, -2.0, -3.6, -4.6, -0.8)
				.bezierCurveTo(-3.5, 3.0, 1.8, 4.1, 5.8, 0)
				.fill({color: light, alpha: 0.90});
		} else {
			particle.circle(-1.4, -0.2, 4.1).fill({color: light, alpha: 0.88});
			particle.circle(2.0, 0.7, 3.3).fill({color: light, alpha: 0.88});
		}
		particle.circle(-1.3, -1.25, 1.15).fill({color: hot, alpha: 0.74});
	}

	spawn(segment, endpoint) {
		if(this.active.length >= MAX_PARTICLES) return;
		const particle = this.pool.pop() || new PIXI.Graphics();
		const droplet = Math.random() < 0.46;
		const tangentDistance = segment.min + Math.random() * (segment.max - segment.min);
		const endpointPosition = endpoint.body.GetPosition();
		const normal = portalNormal(endpoint.body.GetAngle());
		const tangent = portalTangent(endpoint.body.GetAngle());
		const tangentPixels = tangentDistance * this.ptm;
		const normalJitter = (Math.random() - 0.5) * Math.min(3, this.ptm * 0.08);
		const normalDirection = Math.random() < 0.5 ? -1 : 1;
		const normalSpeed = normalDirection * (6 + Math.random() * 16);
		const outwardDirection = tangentDistance === 0 ?
			(Math.random() < 0.5 ? -1 : 1) : Math.sign(tangentDistance);
		const tangentSpeed = outwardDirection * (7 + Math.random() * 20) +
			(Math.random() - 0.5) * 18;

		this.drawParticle(particle, endpointColor(endpoint), droplet);
		particle.position.set(
			endpointPosition.get_x() * this.ptm + tangent.x * tangentPixels + normal.x * normalJitter,
			endpointPosition.get_y() * this.ptm + tangent.y * tangentPixels + normal.y * normalJitter,
		);
		particle.alpha = 1;
		particle.visible = true;
		particle.blendMode = 'normal';
		const baseScale = 0.82 + Math.random() * 0.86;
		particle.scale.set(baseScale * (droplet ? 1.12 : 1), baseScale * (droplet ? 0.82 : 1));
		const data = particle.__portalParticle || (particle.__portalParticle = {});
		data.age = 0;
		data.life = 0.50 + Math.random() * 0.42;
		data.vx = normal.x * normalSpeed + tangent.x * tangentSpeed;
		data.vy = normal.y * normalSpeed + tangent.y * tangentSpeed;
		data.turn = (Math.random() - 0.5) * 1.4;
		data.spin = (Math.random() - 0.5) * 2.4;
		data.wobblePhase = Math.random() * Math.PI * 2;
		data.wobbleSpeed = 7 + Math.random() * 5;
		data.startScaleX = particle.scale.x;
		data.startScaleY = particle.scale.y;
		particle.rotation = Math.atan2(data.vy, data.vx);
		this.container.addChild(particle);
		this.active.push(particle);
	}

	recycle(index) {
		const particle = this.active[index];
		this.active.splice(index, 1);
		particle.removeFromParent();
		particle.visible = false;
		this.pool.push(particle);
	}

	updateParticles(deltaSeconds) {
		const drag = Math.pow(0.965, deltaSeconds * 60);
		for(let index = this.active.length - 1; index >= 0; index--) {
			const particle = this.active[index];
			const data = particle.__portalParticle;
			data.age += deltaSeconds;
			if(data.age >= data.life) {
				this.recycle(index);
				continue;
			}
			const turn = data.turn * deltaSeconds;
			const cosine = Math.cos(turn);
			const sine = Math.sin(turn);
			const vx = data.vx * cosine - data.vy * sine;
			const vy = data.vx * sine + data.vy * cosine;
			data.vx = vx * drag;
			data.vy = vy * drag;
			particle.x += data.vx * deltaSeconds;
			particle.y += data.vy * deltaSeconds;
			particle.rotation += data.spin * deltaSeconds;
			const remaining = 1 - data.age / data.life;
			const wobble = Math.sin(
				data.wobblePhase + data.age * data.wobbleSpeed,
			) * 0.10;
			const fadeScale = 0.55 + remaining * 0.45;
			particle.alpha = Math.min(1, remaining * 2.1);
			particle.scale.set(
				data.startScaleX * fadeScale * (1 + wobble),
				data.startScaleY * fadeScale * (1 - wobble * 0.7),
			);
		}
	}

	update(deltaSeconds = 0) {
		if(!this.container || this.container.destroyed) return;
		const delta = Math.max(0, Math.min(0.05, Number(deltaSeconds) || 0));
		this.syncDepth();
		this.updateParticles(delta);
		if(delta <= 0) return;
		const endpoint = this.getEndpoint?.();
		const bodies = this.getBodies?.();
		const segments = getPortalIntersectionSegments(this.Box2D, bodies, endpoint);
		if(!segments.length) return;
		const totalLength = segments.reduce(
			(total, segment) => total + Math.max(0.02, segment.max - segment.min),
			0,
		);
		const particlesPerSecond = Math.min(120, 52 + totalLength * 18);
		this.spawnAccumulator += particlesPerSecond * delta;
		const spawnCount = Math.min(MAX_SPAWNS_PER_FRAME, Math.floor(this.spawnAccumulator));
		this.spawnAccumulator -= spawnCount;
		for(let index = 0; index < spawnCount; index++) {
			this.spawn(chooseSegment(segments, totalLength), endpoint);
		}
	}

	destroy() {
		for(const particle of [...this.active, ...this.pool]) particle.destroy();
		this.active.length = 0;
		this.pool.length = 0;
		if(this.container && !this.container.destroyed) this.container.destroy({children: false});
		this.container = null;
		this.depthDisplays = [];
		this.getBodies = null;
		this.getEndpoint = null;
	}
}
